import * as THREE from "three";
import { clampShadowMapSize } from "../renderer/qualityCaps";
import { configureStrobeLight } from "./strobeLight.utils";

const resolveShadowMapSize = (light: any, appData: any) => {
    const renderQuality = appData?.renderService?.renderQuality || "MQ";
    return clampShadowMapSize(light?.shadows?.mapSize, renderQuality);
};

/**
 * Creates a Three.js light based on configuration
 * Supports HemisphereLight, AmbientLight, PointLight, and SpotLight
 * @param light Light configuration object
 * @param scene Optional scene to add debug helpers
 * @returns Configured Three.js light object
 */
export function loadLight(light: any, appData: any) {
    if (!light) return;
    let outputLight;
    let outputLightTarget = null;
    let outputLightHelper = null;
    switch (light.type) {
        case "HemisphereLight":
            outputLight = new THREE.HemisphereLight(
                new THREE.Color(light.skyColor),
                new THREE.Color(light.groundColor),
                light.intensity
            );

            break;
        case "AmbientLight":
            outputLight = new THREE.AmbientLight(light.color, light.intensity);

            break;
        case "DirectionalLight":
            outputLight = new THREE.DirectionalLight(
                light.color,
                light.intensity
            );
            outputLight.position.set(
                light.position.x,
                light.position.y,
                light.position.z
            );
            outputLight.target.position.set(
                light.target.x,
                light.target.y,
                light.target.z
            );

            outputLightTarget = outputLight.target;
            // outputLightHelper = new THREE.DirectionalLightHelper(outputLight, light.shadows.frustumLimit ? light.shadows.frustumLimit : 5);

            if (light.castShadow) {
                outputLight.castShadow = light.castShadow;
                const shadowMapSize = resolveShadowMapSize(light, appData);
                outputLight.shadow.mapSize.width = shadowMapSize;
                outputLight.shadow.mapSize.height = shadowMapSize;
                outputLight.shadow.camera.near = light.shadows.near;
                outputLight.shadow.camera.far = light.shadows.far;
                outputLight.shadow.camera.left = -light.shadows.frustumLimit;
                outputLight.shadow.camera.right = light.shadows.frustumLimit;
                outputLight.shadow.camera.top = light.shadows.frustumLimit;
                outputLight.shadow.camera.bottom = -light.shadows.frustumLimit;
                outputLight.shadow.bias = light.shadows.bias;
                outputLight.shadow.normalBias = light.shadows.normalBias;
            }
            break;
        case "PointLight":
            outputLight = new THREE.PointLight(
                light.color,
                light.intensity,
                light.distance,
                light.decay
            );
            outputLight.position.set(
                light.position.x,
                light.position.y,
                light.position.z
            );
            // outputLightHelper = new THREE.PointLightHelper(outputLight);
            outputLightTarget = outputLight.target;

            break;
        case "StrobeLight":
            outputLight = configureStrobeLight(
                new THREE.PointLight(
                    light.color,
                    light.intensity,
                    light.distance,
                    light.decay
                ),
                light.intensity,
                light.frequencyHz
            );
            outputLight.position.set(
                light.position.x,
                light.position.y,
                light.position.z
            );
            appData.strobeLights = Array.isArray(appData.strobeLights) ? appData.strobeLights : [];
            appData.strobeLights.push(outputLight);
            break;
        case "SpotLight":
            outputLight = new THREE.SpotLight(
                light.color,
                light.intensity,
                light.distance,
                light.angle,
                light.penumbra,
                light.decay
            );
            outputLight.position.set(
                light.position.x,
                light.position.y,
                light.position.z
            );
            outputLight.target.position.set(
                light.target.x,
                light.target.y,
                light.target.z
            );

            outputLight.target.updateMatrixWorld();
            // outputLightHelper = new THREE.SpotLightHelper(outputLight);
            outputLightTarget = outputLight.target;

            if (light.castShadow) {
                outputLight.castShadow = light.castShadow;
                const shadowMapSize = resolveShadowMapSize(light, appData);
                outputLight.shadow.mapSize.width = shadowMapSize;
                outputLight.shadow.mapSize.height = shadowMapSize;
                outputLight.shadow.camera.near = light.shadows.near;
                outputLight.shadow.camera.far = light.shadows.far;
                outputLight.shadow.bias = light.shadows.bias;
                outputLight.shadow.fov = light.angle;
                outputLight.shadow.normalBias = light.shadows.normalBias;
            }
            break;
    }


    const lightsHolder = appData.spaceScene.getObjectByName("lightsHolder")
    const lightHelper = appData.spaceScene.getObjectByName("lightHelper")
    const targetsHolder = appData.spaceScene.getObjectByName("targetsHolder")

    lightsHolder.add(outputLight)
    if (outputLightHelper) lightHelper.add(outputLightHelper)
    if (outputLightTarget) targetsHolder.add(outputLightTarget)
}

export function removeLight(index: number, appData: any) {
    const lightsHolder = appData.spaceScene.getObjectByName("lightsHolder")
    const targetsHolder = appData.spaceScene.getObjectByName("targetsHolder")
    const lightHelper = appData.spaceScene.getObjectByName("lightHelper")

    const lightToRemove = lightsHolder?.children?.[index]
    if (lightToRemove) {
        lightsHolder.remove(lightToRemove)
        appData.strobeLights = (appData.strobeLights || []).filter((light: THREE.PointLight) => light !== lightToRemove)
    }

    // Directional/spot lights usually add targets at the same index.
    // Keep holders in sync when removing a light.
    const targetToRemove = targetsHolder?.children?.[index]
    if (targetToRemove) {
        targetsHolder.remove(targetToRemove)
    }

    const helperToRemove = lightHelper?.children?.[index]
    if (helperToRemove) {
        lightHelper.remove(helperToRemove)
    }
}

/**
 * Creates multiple lights from configuration and adds them to a parent object
 * @param lights Array of light configurations
 * @param parent Parent object to add lights to
 */
export function createLights(lights: any, appData: any) {
    if (!lights) return;
    for (let index = 0; index < lights.length; index++) {
        loadLight(lights[index], appData);
    }
}

export function removeLights(appData: any) {
    const lightsHolder = appData.spaceScene.getObjectByName("lightsHolder")
    const lightHelper = appData.spaceScene.getObjectByName("lightHelper")
    const targetsHolder = appData.spaceScene.getObjectByName("targetsHolder")

    while (lightsHolder.children.length > 0) {
        lightsHolder.remove(lightsHolder.children[0])
    }
    while (lightHelper.children.length > 0) {
        lightHelper.remove(lightHelper.children[0])
    }
    while (targetsHolder.children.length > 0) {
        targetsHolder.remove(targetsHolder.children[0])
    }
    appData.strobeLights = []
    // for (let index = lightHelper.children.length - 1; index > 0; index--) {
    //   lightHelper.remove(lightHelper.children[index])
    // }
    // for (let index = targetsHolder.children.length - 1; index > 0; index--) {
    //   targetsHolder.remove(targetsHolder.children[index])
    // }
    // let existingLights = []
    // appData.spaceScene.traverse((child: any) => {
    //   if (child.isLight) {
    //     existingLights.push(child)
    //   }
    // })

    // for (let index = 0; index < existingLights.length; index++) {
    //   const element = existingLights[index];
    //   element.removeFromParent()
    //   element.dispose()
    // }
}

